import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useResponsive } from '../hooks/useResponsive';

const InviteManager = ({ profile, organizations, teams }) => {
  const { isDesktop } = useResponsive();
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);

  const [formData, setFormData] = useState({
    organization_id: organizations[0]?.id || '',
    role: 'player',
    team_id: '',
    expires_in_days: 7,
    max_uses: 1,
  });

  useEffect(() => {
    loadInvites();
  }, []);

  const loadInvites = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('invites')
        .select(
          `
          *,
          organizations(name),
          teams(name),
          created_by_profile:profiles!invites_created_by_fkey(full_name)
        `
        )
        .order('created_at', { ascending: false });

      if (error) throw error;
      setInvites(data || []);
    } catch (err) {
      console.error('Error loading invites:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInvite = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      // Generate invite code
      const { data: codeData, error: codeError } = await supabase.rpc('generate_invite_code');

      if (codeError) throw codeError;

      const code = codeData;

      // Get current user
      const {
        data: { user },
      } = await supabase.auth.getUser();

      // Calculate expiration date
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + parseInt(formData.expires_in_days));

      // Create invite
      const { error: insertError } = await supabase.from('invites').insert({
        code,
        organization_id: formData.organization_id,
        role: formData.role,
        team_id: formData.team_id || null,
        expires_at: expiresAt.toISOString(),
        max_uses: parseInt(formData.max_uses),
        created_by: user.id,
      });

      if (insertError) throw insertError;

      setSuccess(`Invite created! Code: ${code}`);
      setShowCreateForm(false);
      loadInvites();

      // Reset form
      setFormData({
        organization_id: organizations[0]?.id || '',
        role: 'player',
        team_id: '',
        expires_in_days: 7,
        max_uses: 1,
      });
    } catch (err) {
      console.error('Error creating invite:', err);
      setError(err.message);
    }
  };

  const handleRevokeInvite = async (inviteId) => {
    if (!window.confirm('Are you sure you want to revoke this invite?')) {
      return;
    }

    try {
      const { error } = await supabase.from('invites').delete().eq('id', inviteId);

      if (error) throw error;

      setSuccess('Invite revoked successfully');
      loadInvites();
    } catch (err) {
      console.error('Error revoking invite:', err);
      setError(err.message);
    }
  };

  const copyInviteLink = (code) => {
    const link = `${window.location.origin}/login?invite=${code}`;
    navigator.clipboard.writeText(link);
    setSuccess(`Invite link copied to clipboard!`);
    setTimeout(() => setSuccess(''), 3000);
  };

  const isExpired = (expiresAt) => {
    return new Date(expiresAt) < new Date();
  };

  const isFullyUsed = (invite) => {
    return invite.used_count >= invite.max_uses;
  };

  const getInviteStatus = (invite) => {
    if (isExpired(invite.expires_at)) return 'Expired';
    if (isFullyUsed(invite)) return 'Fully Used';
    return 'Active';
  };

  const styles = {
    container: {
      marginTop: isDesktop ? '40px' : '20px',
    },
    header: {
      display: 'flex',
      flexDirection: isDesktop ? 'row' : 'column',
      justifyContent: 'space-between',
      alignItems: isDesktop ? 'center' : 'stretch',
      marginBottom: '20px',
      gap: isDesktop ? '0' : '12px',
    },
    title: {
      fontSize: isDesktop ? '24px' : '20px',
      fontWeight: 'bold',
      color: '#333',
      margin: 0,
    },
    button: {
      padding: isDesktop ? '10px 20px' : '14px 20px',
      fontSize: '14px',
      fontWeight: '500',
      color: 'white',
      backgroundColor: '#007bff',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      minHeight: '44px',
    },
    secondaryButton: {
      padding: isDesktop ? '8px 16px' : '10px 16px',
      fontSize: isDesktop ? '13px' : '14px',
      fontWeight: '500',
      color: '#007bff',
      backgroundColor: 'white',
      border: '1px solid #007bff',
      borderRadius: '4px',
      cursor: 'pointer',
      marginRight: '8px',
      minHeight: '44px',
    },
    dangerButton: {
      padding: isDesktop ? '8px 16px' : '10px 16px',
      fontSize: isDesktop ? '13px' : '14px',
      fontWeight: '500',
      color: 'white',
      backgroundColor: '#dc3545',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      minHeight: '44px',
    },
    form: {
      backgroundColor: 'white',
      borderRadius: '8px',
      padding: isDesktop ? '20px' : '16px',
      marginBottom: '20px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    },
    formRow: {
      display: 'grid',
      gridTemplateColumns: isDesktop ? 'repeat(auto-fit, minmax(200px, 1fr))' : '1fr',
      gap: '16px',
      marginBottom: '16px',
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
    },
    label: {
      fontSize: '14px',
      fontWeight: '500',
      color: '#333',
    },
    input: {
      padding: '8px 12px',
      fontSize: '14px',
      border: '1px solid #ddd',
      borderRadius: '4px',
    },
    select: {
      padding: '8px 12px',
      fontSize: '14px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      backgroundColor: 'white',
    },
    formActions: {
      display: 'flex',
      gap: '10px',
      justifyContent: 'flex-end',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      backgroundColor: 'white',
      borderRadius: '8px',
      overflow: 'hidden',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    },
    th: {
      backgroundColor: '#f8f9fa',
      padding: '12px',
      textAlign: 'left',
      fontWeight: '600',
      fontSize: '14px',
      color: '#333',
      borderBottom: '2px solid #dee2e6',
    },
    td: {
      padding: '12px',
      borderBottom: '1px solid #dee2e6',
      fontSize: '14px',
      color: '#333',
    },
    badge: {
      padding: '4px 8px',
      borderRadius: '4px',
      fontSize: '12px',
      fontWeight: '500',
      display: 'inline-block',
    },
    badgeActive: {
      backgroundColor: '#d4edda',
      color: '#155724',
    },
    badgeExpired: {
      backgroundColor: '#f8d7da',
      color: '#721c24',
    },
    badgeUsed: {
      backgroundColor: '#d1ecf1',
      color: '#0c5460',
    },
    error: {
      padding: '12px',
      backgroundColor: '#fee',
      color: '#c33',
      borderRadius: '4px',
      marginBottom: '16px',
      fontSize: '14px',
      border: '1px solid #fcc',
    },
    success: {
      padding: '12px',
      backgroundColor: '#efe',
      color: '#3c3',
      borderRadius: '4px',
      marginBottom: '16px',
      fontSize: '14px',
      border: '1px solid #cfc',
    },
    codeCell: {
      fontFamily: 'monospace',
      fontSize: '13px',
      fontWeight: 'bold',
      color: '#007bff',
    },
    inviteCard: {
      backgroundColor: 'white',
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '12px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      border: '1px solid #e0e0e0',
    },
    cardHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '12px',
      paddingBottom: '12px',
      borderBottom: '1px solid #f0f0f0',
    },
    cardCode: {
      fontFamily: 'monospace',
      fontSize: '16px',
      fontWeight: 'bold',
      color: '#007bff',
    },
    cardBody: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      marginBottom: '12px',
      fontSize: '14px',
    },
    cardRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    cardLabel: {
      fontWeight: '600',
      color: '#666',
    },
    cardValue: {
      color: '#333',
    },
    cardActions: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
    },
    inviteCardsContainer: {
      display: 'flex',
      flexDirection: 'column',
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>Invite Management</h2>
        <button style={styles.button} onClick={() => setShowCreateForm(!showCreateForm)}>
          {showCreateForm ? 'Cancel' : 'Create New Invite'}
        </button>
      </div>

      {error && <div style={styles.error}>{error}</div>}
      {success && <div style={styles.success}>{success}</div>}

      {showCreateForm && (
        <form onSubmit={handleCreateInvite} style={styles.form}>
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Organization</label>
              <select
                style={styles.select}
                value={formData.organization_id}
                onChange={(e) => setFormData({ ...formData, organization_id: e.target.value })}
                required
              >
                <option value="">Select Organization</option>
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Role</label>
              <select
                style={styles.select}
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                required
              >
                <option value="player">Player</option>
                <option value="parent">Parent</option>
                <option value="coach">Coach</option>
                <option value="admin">Admin</option>
                {profile?.role === 'super_admin' && (
                  <option value="super_admin">Super Admin</option>
                )}
              </select>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Team (Optional)</label>
              <select
                style={styles.select}
                value={formData.team_id}
                onChange={(e) => setFormData({ ...formData, team_id: e.target.value })}
              >
                <option value="">No Team</option>
                {teams
                  .filter((team) => team.organization_id === formData.organization_id)
                  .map((team) => (
                    <option key={team.id} value={team.id}>
                      {team.name}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Expires In (Days)</label>
              <input
                type="number"
                style={styles.input}
                value={formData.expires_in_days}
                onChange={(e) => setFormData({ ...formData, expires_in_days: e.target.value })}
                min="1"
                max="365"
                required
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Max Uses</label>
              <input
                type="number"
                style={styles.input}
                value={formData.max_uses}
                onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                min="1"
                max="100"
                required
              />
            </div>
          </div>

          <div style={styles.formActions}>
            <button type="submit" style={styles.button}>
              Generate Invite
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div>Loading invites...</div>
      ) : isDesktop ? (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Code</th>
              <th style={styles.th}>Organization</th>
              <th style={styles.th}>Role</th>
              <th style={styles.th}>Team</th>
              <th style={styles.th}>Uses</th>
              <th style={styles.th}>Expires</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Created By</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {invites.length === 0 ? (
              <tr>
                <td colSpan="9" style={{ ...styles.td, textAlign: 'center', padding: '40px' }}>
                  No invites found. Create one to get started!
                </td>
              </tr>
            ) : (
              invites.map((invite) => {
                const status = getInviteStatus(invite);
                const badgeStyle =
                  status === 'Active'
                    ? styles.badgeActive
                    : status === 'Expired'
                      ? styles.badgeExpired
                      : styles.badgeUsed;

                return (
                  <tr key={invite.id}>
                    <td style={{ ...styles.td, ...styles.codeCell }}>{invite.code}</td>
                    <td style={styles.td}>{invite.organizations?.name || 'N/A'}</td>
                    <td style={styles.td}>{invite.role}</td>
                    <td style={styles.td}>{invite.teams?.name || 'N/A'}</td>
                    <td style={styles.td}>
                      {invite.used_count} / {invite.max_uses}
                    </td>
                    <td style={styles.td}>{new Date(invite.expires_at).toLocaleDateString()}</td>
                    <td style={styles.td}>
                      <span style={{ ...styles.badge, ...badgeStyle }}>{status}</span>
                    </td>
                    <td style={styles.td}>{invite.created_by_profile?.full_name || 'N/A'}</td>
                    <td style={styles.td}>
                      {status === 'Active' && (
                        <>
                          <button
                            style={styles.secondaryButton}
                            onClick={() => copyInviteLink(invite.code)}
                          >
                            Copy Link
                          </button>
                          <button
                            style={styles.dangerButton}
                            onClick={() => handleRevokeInvite(invite.id)}
                          >
                            Revoke
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      ) : (
        <div style={styles.inviteCardsContainer}>
          {invites.length === 0 ? (
            <div
              style={{ ...styles.inviteCard, textAlign: 'center', padding: '40px', color: '#999' }}
            >
              No invites found. Create one to get started!
            </div>
          ) : (
            invites.map((invite) => {
              const status = getInviteStatus(invite);
              const badgeStyle =
                status === 'Active'
                  ? styles.badgeActive
                  : status === 'Expired'
                    ? styles.badgeExpired
                    : styles.badgeUsed;

              return (
                <div key={invite.id} style={styles.inviteCard}>
                  <div style={styles.cardHeader}>
                    <div style={styles.cardCode}>{invite.code}</div>
                    <span style={{ ...styles.badge, ...badgeStyle }}>{status}</span>
                  </div>

                  <div style={styles.cardBody}>
                    <div style={styles.cardRow}>
                      <span style={styles.cardLabel}>Organization:</span>
                      <span style={styles.cardValue}>{invite.organizations?.name || 'N/A'}</span>
                    </div>
                    <div style={styles.cardRow}>
                      <span style={styles.cardLabel}>Role:</span>
                      <span style={styles.cardValue}>{invite.role}</span>
                    </div>
                    <div style={styles.cardRow}>
                      <span style={styles.cardLabel}>Team:</span>
                      <span style={styles.cardValue}>{invite.teams?.name || 'N/A'}</span>
                    </div>
                    <div style={styles.cardRow}>
                      <span style={styles.cardLabel}>Uses:</span>
                      <span style={styles.cardValue}>
                        {invite.used_count} / {invite.max_uses}
                      </span>
                    </div>
                    <div style={styles.cardRow}>
                      <span style={styles.cardLabel}>Expires:</span>
                      <span style={styles.cardValue}>
                        {new Date(invite.expires_at).toLocaleDateString()}
                      </span>
                    </div>
                    <div style={styles.cardRow}>
                      <span style={styles.cardLabel}>Created By:</span>
                      <span style={styles.cardValue}>
                        {invite.created_by_profile?.full_name || 'N/A'}
                      </span>
                    </div>
                  </div>

                  {status === 'Active' && (
                    <div style={styles.cardActions}>
                      <button
                        style={{ ...styles.secondaryButton, marginRight: 0, width: '100%' }}
                        onClick={() => copyInviteLink(invite.code)}
                      >
                        Copy Link
                      </button>
                      <button
                        style={{ ...styles.dangerButton, width: '100%' }}
                        onClick={() => handleRevokeInvite(invite.id)}
                      >
                        Revoke
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default InviteManager;
